import { loadTranslateSettings, saveTranslateSettings } from '../translate/translateSettings';
import { TranslateError, translateText } from '../translate/translateApi';
import { appAlert } from '../utils/appDialog';
import {
  loadCursorAgentPreferences,
  saveCursorAgentPreferences,
} from '../utils/cursorAgentPreferences';
import {
  checkNetlifyDeploy,
  formatNetlifyDeployStatus,
} from '../services/netlifyDeployApi';
import {
  loadNetlifyDeployPreferences,
  saveNetlifyDeployPreferences,
} from '../utils/netlifyDeployPreferences';
import { fetchLichessAccount } from '../services/lichessApi';
import {
  loadLichessPreferences,
  saveLichessPreferences,
} from '../utils/lichessPreferences';
import { formatNetlifyEnvExport } from '../utils/netlifyEnvExport';
import { copyTextToClipboard } from '../utils/formatListClipboard';
import { flashCopyOnButton } from '../utils/copyFeedback';
import { generateAndCopyDeviceSettingsShareLink } from '../utils/appLocalStorageBackupCrypto';
import { testCursorAgentApiKey } from '../services/cursorAgentApi';

export type ConfigViewHooks = {
  onOpenImport?: () => void;
};

/** App settings (translation, etc.). Stored in localStorage only. */
export class ConfigView {
  render(container: HTMLElement, hooks: ConfigViewHooks = {}): void {
    const settings = loadTranslateSettings();
    const cursorAgent = loadCursorAgentPreferences();
    const netlify = loadNetlifyDeployPreferences();
    const lichess = loadLichessPreferences();
    container.innerHTML = `
      <div class="config-view">
        <header class="config-view-header">
          <h2 class="file-title">Config</h2>
          <p class="config-view-lead text-muted small">
            Device-only settings stored in <code>localStorage</code>. Expand a section to edit.
          </p>
        </header>
        <form class="config-view-form" id="config-form">
          <details class="config-panel">
            <summary class="config-panel-summary">Language translation</summary>
            <div class="config-panel-body">
              <p class="config-hint text-muted small">
                Translation is enabled on the <strong>Spanish</strong> page only (<code>database/spanish.md</code>). Open an item, switch to reading mode, then highlight a word or long-press — the surrounding sentence is translated in the bubble.
              </p>
              <p class="config-hint text-muted small">
                Uses the free <a href="https://mymemory.translated.net/doc/spec.php" target="_blank" rel="noopener noreferrer">MyMemory</a> API from your browser — no API key or paid account. About 5,000 characters per day per device; optional email below raises that to 50,000 (still free, no signup).
              </p>
              <label class="config-label" for="config-translate-email">Email (optional, for higher free quota)</label>
              <input
                type="email"
                id="config-translate-email"
                class="config-input"
                value="${escapeAttr(settings.contactEmail)}"
                placeholder="you@example.com"
                autocomplete="email"
              />
              <div class="config-actions">
                <button type="button" class="btn-ghost-dark" id="config-test-translate">Test translation</button>
              </div>
            </div>
          </details>

          <details class="config-panel">
            <summary class="config-panel-summary">AI Agent</summary>
            <div class="config-panel-body">
          <p class="config-hint text-muted small">
            API key for the <strong>AI Agent</strong> sidebar chat. Stored in <code>localStorage</code> on this device only.
            <a href="https://cursor.com/dashboard?tab=cloud-agents" target="_blank" rel="noopener noreferrer">Get a key</a>.
          </p>
          <label class="config-label" for="config-cursor-api-key">Cursor API key</label>
          <input
            type="password"
            id="config-cursor-api-key"
            class="config-input"
            value="${escapeAttr(cursorAgent.apiKey)}"
            placeholder="Cloud Agents API key"
            autocomplete="off"
          />
          <label class="config-label" for="config-cursor-repo">GitHub repo URL (optional)</label>
          <input
            type="url"
            id="config-cursor-repo"
            class="config-input"
            value="${escapeAttr(cursorAgent.repoUrl)}"
            placeholder="https://github.com/you/DailyLife"
            autocomplete="off"
          />
          <div class="config-actions">
            <button type="button" class="btn-ghost-dark" id="config-test-cursor">Test AI Agent connection</button>
          </div>
            </div>
          </details>

          <details class="config-panel">
            <summary class="config-panel-summary">Netlify deploy</summary>
            <div class="config-panel-body">
              <p class="config-hint text-muted small">
            Monitor production deploys after merges. Stored in <code>localStorage</code> on this device only.
            For <strong>automatic</strong> access in Cursor Cloud agents, add the same values in
            <a href="https://cursor.com/dashboard?tab=cloud-agents" target="_blank" rel="noopener noreferrer">Cursor Cloud → Secrets</a>
            as <code>NETLIFY_AUTH_TOKEN</code> and <code>NETLIFY_SITE_URL</code> (see <code>docs/cursor-cloud-secrets.md</code>).
            <a href="https://app.netlify.com/user/applications#personal-access-tokens" target="_blank" rel="noopener noreferrer">Create a Netlify token</a>.
          </p>
          <label class="config-label" for="config-netlify-token">Netlify API token</label>
          <input
            type="password"
            id="config-netlify-token"
            class="config-input"
            value="${escapeAttr(netlify.apiToken)}"
            placeholder="Personal access token"
            autocomplete="off"
          />
          <label class="config-label" for="config-netlify-site">Project / site URL</label>
          <input
            type="url"
            id="config-netlify-site"
            class="config-input"
            value="${escapeAttr(netlify.siteUrl)}"
            placeholder="https://codenesium.netlify.app"
            autocomplete="off"
          />
          <div class="config-actions">
            <button type="button" class="btn-ghost-dark" id="config-check-netlify">Check latest deploy</button>
            <button type="button" class="btn-ghost-dark" id="config-copy-netlify-env">Copy for cloud agent</button>
          </div>
            </div>
          </details>

          <details class="config-panel">
            <summary class="config-panel-summary">Lichess</summary>
            <div class="config-panel-body">
              <p class="config-hint text-muted small">
                Personal access token for your last games on the <strong>Chess</strong> page.
                Stored in <code>localStorage</code> on this device only.
                <a href="https://lichess.org/account/oauth/token" target="_blank" rel="noopener noreferrer">Create a token</a>
                (no scopes required for reading your games).
              </p>
              <label class="config-label" for="config-lichess-token">Lichess API token</label>
              <input
                type="password"
                id="config-lichess-token"
                class="config-input"
                value="${escapeAttr(lichess.apiToken)}"
                placeholder="lip_…"
                autocomplete="off"
              />
              <label class="config-label" for="config-lichess-username">Username (optional)</label>
              <input
                type="text"
                id="config-lichess-username"
                class="config-input"
                value="${escapeAttr(lichess.username)}"
                placeholder="Filled from account when empty"
                autocomplete="username"
              />
              <div class="config-actions">
                <button type="button" class="btn-ghost-dark" id="config-test-lichess">Test Lichess connection</button>
              </div>
            </div>
          </details>

          <details class="config-panel">
            <summary class="config-panel-summary">Device backup</summary>
            <div class="config-panel-body">
              <p class="config-hint text-muted small">
            Copy an encrypted link (same as Tools → Encrypt link). Passphrase is not in the URL — share it separately.
            Settings only (no AI chat history). On the other device, open the link, decrypt, then import.
          </p>
          <div class="config-actions">
            <button type="button" class="btn-ghost-dark" id="config-copy-local-backup">Copy encrypted link</button>
            <button type="button" class="btn-ghost-dark" id="config-open-import">Import settings…</button>
          </div>
            </div>
          </details>

          <div class="config-actions config-actions-save">
            <button type="submit" class="btn-primary" id="config-save">Save</button>
          </div>
          <p class="config-status text-muted small" id="config-status" aria-live="polite"></p>
        </form>
      </div>
    `;

    const form = container.querySelector<HTMLFormElement>('#config-form')!;
    const emailInput = container.querySelector<HTMLInputElement>('#config-translate-email')!;
    const cursorKeyInput = container.querySelector<HTMLInputElement>('#config-cursor-api-key')!;
    const cursorRepoInput = container.querySelector<HTMLInputElement>('#config-cursor-repo')!;
    const netlifyTokenInput = container.querySelector<HTMLInputElement>('#config-netlify-token')!;
    const netlifySiteInput = container.querySelector<HTMLInputElement>('#config-netlify-site')!;
    const lichessTokenInput = container.querySelector<HTMLInputElement>('#config-lichess-token')!;
    const lichessUserInput = container.querySelector<HTMLInputElement>('#config-lichess-username')!;
    const status = container.querySelector<HTMLElement>('#config-status')!;

    const persistAll = (): void => {
      saveTranslateSettings({ contactEmail: emailInput.value });
      saveCursorAgentPreferences({
        apiKey: cursorKeyInput.value.trim(),
        repoUrl: cursorRepoInput.value.trim(),
        sessionToken: '',
        adminApiKey: '',
      });
      saveNetlifyDeployPreferences({
        apiToken: netlifyTokenInput.value.trim(),
        siteUrl: netlifySiteInput.value.trim(),
      });
      saveLichessPreferences({
        apiToken: lichessTokenInput.value.trim(),
        username: lichessUserInput.value.trim(),
      });
    };

    form.addEventListener('submit', e => {
      e.preventDefault();
      persistAll();
      status.textContent = 'Saved translation, AI Agent, Netlify, and Lichess settings.';
    });

    container.querySelector('#config-test-translate')!.addEventListener('click', () => {
      void (async () => {
        status.textContent = 'Testing…';
        try {
          const result = await translateText('Buenos días', {
            contactEmail: emailInput.value,
            allowOutsideSpanishPage: true,
          });
          status.textContent = `Test OK: "${result}"`;
        } catch (err) {
          const msg = err instanceof TranslateError ? err.message : 'Test failed.';
          status.textContent = msg;
          await appAlert(msg, 'Translation test failed');
        }
      })();
    });

    container.querySelector('#config-copy-netlify-env')!.addEventListener('click', () => {
      void (async () => {
        try {
          const text = formatNetlifyEnvExport(
            netlifyTokenInput.value,
            netlifySiteInput.value
          );
          await copyTextToClipboard(text);
          status.textContent = 'Copied env lines — paste into Cursor Cloud → Secrets.';
        } catch (err) {
          status.textContent =
            err instanceof Error ? err.message : 'Could not copy to clipboard.';
        }
      })();
    });

    container.querySelector('#config-copy-local-backup')!.addEventListener('click', e => {
      void (async () => {
        const btn = e.currentTarget as HTMLButtonElement;
        const url = await generateAndCopyDeviceSettingsShareLink();
        if (url) {
          status.textContent = 'Encrypted settings link copied (passphrase not in URL).';
          flashCopyOnButton(btn, 'Copied encrypted link');
        }
      })();
    });

    container.querySelector('#config-open-import')!.addEventListener('click', () => {
      hooks.onOpenImport?.();
    });

    container.querySelector('#config-test-cursor')!.addEventListener('click', () => {
      void (async () => {
        const key = cursorKeyInput.value.trim();
        if (!key) {
          status.textContent = 'Enter a Cursor API key first.';
          return;
        }
        status.textContent = 'Testing Cursor proxy…';
        try {
          await testCursorAgentApiKey(key);
          persistAll();
          status.textContent = 'Cursor OK — /api/cursor-agent and API key work.';
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Cursor test failed.';
          status.textContent = msg;
          await appAlert(msg, 'AI Agent connection failed');
        }
      })();
    });

    container.querySelector('#config-test-lichess')!.addEventListener('click', () => {
      void (async () => {
        const token = lichessTokenInput.value.trim();
        if (!token) {
          status.textContent = 'Enter a Lichess API token first.';
          return;
        }
        status.textContent = 'Testing Lichess…';
        try {
          const account = await fetchLichessAccount(token);
          if (!lichessUserInput.value.trim()) lichessUserInput.value = account.username;
          persistAll();
          status.textContent = `Lichess OK — signed in as ${account.username}.`;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Lichess test failed.';
          status.textContent = msg;
          await appAlert(msg, 'Lichess connection failed');
        }
      })();
    });

    container.querySelector('#config-check-netlify')!.addEventListener('click', () => {
      void (async () => {
        const token = netlifyTokenInput.value.trim();
        const siteUrl = netlifySiteInput.value.trim();
        if (!token || !siteUrl) {
          status.textContent = 'Enter Netlify API token and site URL first.';
          return;
        }
        status.textContent = 'Checking Netlify…';
        try {
          const result = await checkNetlifyDeploy(token, siteUrl);
          persistAll();
          status.textContent = formatNetlifyDeployStatus(result);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Netlify check failed.';
          status.textContent = msg;
          await appAlert(msg, 'Netlify deploy check failed');
        }
      })();
    });
  }
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}
